import React, { useEffect, useMemo, useRef, useState } from 'react';
import { parseListText } from '@battleops/shared';
import { useTaskList, useListTemplates } from '@battleops/client/src/data/useLists';
import Icon from './Icon';
import { prompt, confirm } from './Dialog';
import s from './TaskList.module.css';

// A task's LIST (v0.1.227) — the side panel (TaskDetail) and the full view (TaskModal). Items are information, not
// work: a click ticks, the text edits in place, the line at the bottom adds ("eggs x12", or "milk, eggs, wraps").
// Add from template (search; the task's department first, then most used), Save as template, ⋯ → Make tasks from
// unticked, Clear ticked, Remove list. Never time, never a completion.
export default function TaskList({ task, departmentId = null, heading = null }) {
  const L = useTaskList(task);
  const [picking, setPicking] = useState(false);
  const [menu, setMenu] = useState(false);
  const items = L.list?.items || [];
  const { done, total, left } = L.progress;

  const add = (v) => {
    const parsed = parseListText(v);
    if (!parsed.length) return;
    if (parsed.length === 1) L.add(parsed[0].text, parsed[0].qty ?? null); else L.addMany(parsed);
  };
  const saveAs = async () => {
    const name = await prompt({ title: 'Save as list template', message: `${total} item${total === 1 ? '' : 's'}, ticks cleared. Use it again from “Add from template”.`, value: L.list?.name || task.title, required: true, confirmLabel: 'Save' });
    if (name && name.trim()) L.saveAsTemplate({ name: name.trim(), departmentId });
  };

  return (
    <div className={s.root}>
      {heading ? heading({ done, total }) : null}
      {total ? (
        <div className={s.bar}>
          <div className={s.track}><span style={{ width: `${(done / total) * 100}%` }} data-all={!left || undefined} /></div>
          <span className={`num ${s.count}`} data-all={!left || undefined}>{done} of {total}</span>
          <div className={s.moreWrap}>
            <button className="btn ghost sm icon" onClick={() => setMenu((v) => !v)} title="List actions" aria-label="List actions"><Icon name="more-horizontal" size={15} /></button>
            {menu ? (
              <Menu onClose={() => setMenu(false)} items={[
                { key: 'tasks', icon: 'layers', label: `Make tasks from unticked${left ? ` · ${left}` : ''}`, hint: 'Each becomes a subtask of this task', disabled: !left, run: () => L.toTasks('subtasks') },
                { key: 'clear', icon: 'check-circle', label: `Clear ticked${done ? ` · ${done}` : ''}`, disabled: !done, run: () => L.clearTicked() },
                { key: 'remove', icon: 'trash-2', label: 'Remove list', danger: true, run: async () => { if (await confirm({ title: 'Remove this list?', message: `${total} item${total === 1 ? '' : 's'} go with it.`, danger: true })) L.removeList(); } },
              ]} />
            ) : null}
          </div>
        </div>
      ) : null}
      {L.list?.name ? <div className={s.name}>{L.list.name}</div> : null}
      {items.map((it) => (
        <div key={it.id} className={s.item} data-done={it.done || undefined}>
          <button className={s.check} data-done={it.done || undefined} onClick={() => L.tick(it.id, !it.done)} aria-label={it.done ? `Untick ${it.text}` : `Tick ${it.text}`}>
            {it.done ? <Icon name="check" size={12} /> : null}
          </button>
          <ItemText value={it.text} done={it.done} onSave={(t) => L.edit(it.id, { text: t })} />
          <QtyChip value={it.qty} onSave={(q) => L.edit(it.id, { qty: q })} />
          <button className={`btn ghost sm icon ${s.x}`} title="Remove item" onClick={() => L.remove(it.id)}><Icon name="x" size={12} /></button>
        </div>
      ))}
      <AddItem placeholder={total ? 'Add item…  (eggs x12)' : 'Start a list…  (milk, eggs, wraps)'} onAdd={add} />
      <div className={s.actions}>
        <div className={s.pickWrap}>
          <button className="btn ghost sm" onClick={() => setPicking((v) => !v)}><Icon name="copy" size={13} />Add from template</button>
          {picking ? <TemplatePicker departmentId={departmentId} onClose={() => setPicking(false)} onPick={(t) => { setPicking(false); L.attach(t.id); }} /> : null}
        </div>
        {total ? <button className="btn ghost sm" onClick={saveAs}><Icon name="bookmark" size={13} />Save as template</button> : null}
      </div>
    </div>
  );
}

function ItemText({ value, onSave, done }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return <input className={s.text} data-done={done || undefined} value={v} onChange={(e) => setV(e.target.value)} aria-label="Item"
    onBlur={() => { const t = v.trim(); if (t && t !== value) onSave(t); else setV(value); }}
    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setV(value); e.currentTarget.blur(); } }} />;
}

function QtyChip({ value, onSave }) {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(value ?? '');
  useEffect(() => setV(value ?? ''), [value]);
  if (!edit) {
    return value != null
      ? <button className={s.qty} onClick={() => setEdit(true)} title="Quantity">{typeof value === 'number' ? `×${value}` : value}</button>
      : <button className={s.qtyAdd} onClick={() => setEdit(true)} title="Add a quantity">qty</button>;
  }
  const done = () => { setEdit(false); const q = String(v).trim(); if (q !== String(value ?? '')) onSave(q || null); };
  return <input className={s.qtyInput} autoFocus value={v} onChange={(e) => setV(e.target.value)} onBlur={done} onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setV(value ?? ''); setEdit(false); } }} aria-label="Quantity" />;
}

function AddItem({ placeholder, onAdd }) {
  const [v, setV] = useState('');
  return (
    <div className={s.add}>
      <Icon name="plus" size={13} />
      <input value={v} placeholder={placeholder} onChange={(e) => setV(e.target.value)} aria-label="Add a list item"
        onKeyDown={(e) => { if (e.key === 'Enter' && v.trim()) { onAdd(v.trim()); setV(''); } }}
        onPaste={(e) => { const t = e.clipboardData.getData('text'); if (t.includes('\n')) { e.preventDefault(); onAdd(t); } }} />
    </div>
  );
}

function useOutside(ref, onClose) {
  useEffect(() => {
    const out = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const key = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('mousedown', out, true);
    document.addEventListener('keydown', key, true);
    return () => { document.removeEventListener('mousedown', out, true); document.removeEventListener('keydown', key, true); };
  }, [ref, onClose]);
}

function Menu({ items, onClose }) {
  const ref = useRef(null);
  useOutside(ref, onClose);
  return (
    <div ref={ref} className={s.menu} role="menu">
      {items.map((it) => (
        <button key={it.key} role="menuitem" className={s.menuItem} disabled={it.disabled} data-danger={it.danger || undefined} onClick={() => { onClose(); it.run(); }}>
          <Icon name={it.icon} size={14} /><span>{it.label}{it.hint ? <small>{it.hint}</small> : null}</span>
        </button>
      ))}
    </div>
  );
}

function TemplatePicker({ departmentId, onPick, onClose }) {
  const ref = useRef(null);
  useOutside(ref, onClose);
  const { ranked, templates } = useListTemplates();
  const [q, setQ] = useState('');
  const rows = useMemo(() => ranked(departmentId, q), [ranked, departmentId, q]);
  return (
    <div ref={ref} className={s.picker}>
      <input className="input" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search lists or items" aria-label="Search list templates" />
      <div className={s.pickList}>
        {rows.map((t) => (
          <button key={t.id} className={s.pickRow} onClick={() => onPick(t)}>
            <span className={s.pickIcon}>{t.icon || '📝'}</span>
            <span className={s.pickMain}><b>{t.name}</b><small>{(t.items || []).slice(0, 4).map((x) => x.text).join(', ')}{(t.items || []).length > 4 ? '…' : ''}</small></span>
            <span className={`faint num ${s.pickMeta}`}>{(t.items || []).length} items{departmentId && t.departmentId === departmentId ? ' · dept' : t.uses ? ` · ×${t.uses}` : ''}</span>
          </button>
        ))}
        {!rows.length ? <div className="faint" style={{ padding: 10, fontSize: 12.5 }}>{templates.length ? 'No list matches.' : 'No list templates yet — Setup → Templates, or Save as template from a list.'}</div> : null}
      </div>
    </div>
  );
}
